import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

interface Tab {
  id: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  badge?: string | number;
}

interface OrderDetailTabsProps {
  tabs: Tab[];
  defaultTab?: string;
}

const OrderDetailTabs: React.FC<OrderDetailTabsProps> = ({ tabs, defaultTab }) => {
  const [activeTab, setActiveTab] = useState(defaultTab || tabs[0]?.id || '');
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set([defaultTab || tabs[0]?.id || ''])
  );

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <>
      {/* Desktop: Tabs */}
      <div className="hidden lg:block">
        {/* Tab Headers */}
        <div className="flex border-b border-slate-200 dark:border-slate-700 mb-6 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap transition-all border-b-2 -mb-px ${
                activeTab === tab.id
                  ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-50/50 dark:bg-orange-900/10'
                  : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/50'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
              {tab.badge !== undefined && (
                <span
                  className={`ml-1.5 px-2 py-0.5 text-xs rounded-full ${
                    activeTab === tab.id
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400'
                      : 'bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                  }`}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="min-h-[200px]">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={activeTab === tab.id ? 'block' : 'hidden'}
            >
              {tab.content}
            </div>
          ))}
        </div>
      </div>

      {/* Mobile: Accordion */}
      <div className="lg:hidden space-y-3">
        {tabs.map((tab) => {
          const isExpanded = expandedSections.has(tab.id);

          return (
            <div
              key={tab.id}
              className="bg-white dark:bg-slate-900 rounded-xl border-2 border-slate-200 dark:border-slate-700 overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                onClick={() => toggleSection(tab.id)}
                className={`w-full flex items-center justify-between gap-3 p-4 text-left transition-colors ${
                  isExpanded
                    ? 'bg-orange-50/50 dark:bg-orange-900/10 border-b border-slate-200 dark:border-slate-700'
                    : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`${
                      isExpanded
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-slate-400 dark:text-slate-500'
                    }`}
                  >
                    {tab.icon}
                  </span>
                  <span
                    className={`font-medium ${
                      isExpanded
                        ? 'text-orange-600 dark:text-orange-400'
                        : 'text-slate-700 dark:text-slate-300'
                    }`}
                  >
                    {tab.label}
                  </span>
                  {tab.badge !== undefined && (
                    <span className="px-2 py-0.5 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400">
                      {tab.badge}
                    </span>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronUp className="w-5 h-5 text-orange-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-slate-400" />
                )}
              </button>

              {/* Accordion Content */}
              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isExpanded ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="p-4">{tab.content}</div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

export default OrderDetailTabs;
